import {useMemo, useState} from "react";
import {
    Stack, Typography, Chip, Tooltip, Divider, Paper, Box, IconButton, useMediaQuery
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

function stringifySafe(v) {
    if (v == null) return "—";
    if (typeof v === "string") return v;
    try { return JSON.stringify(v); } catch { return String(v); }
}

// Чип с обрезкой + тултипом + копированием и возможностью развернуть
export function ChipValue({ text }) {
    const [expanded, setExpanded] = useState(false);
    const isLong = text.length > 80;

    if (expanded) {
        return (
            <Paper variant="outlined" sx={{ p: 1, maxWidth: 1 }}>
                <Box
                    component="pre"
                    sx={{ m: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace", fontSize: 12 }}
                >
                    {text}
                </Box>
            </Paper>
        );
    }

    const copy = () => navigator.clipboard?.writeText(text).catch(() => {});
    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ maxWidth: 1 }}>
            <Tooltip title={text} placement="top" disableInteractive>
                <Chip
                    variant="outlined"
                    size="small"
                    label={text}
                    onClick={() => isLong && setExpanded(true)}
                    sx={{
                        maxWidth: 360,
                        "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        },
                    }}
                />
            </Tooltip>
            <IconButton size="small" onClick={copy} aria-label="Скопировать">
                <ContentCopyIcon fontSize="inherit" />
            </IconButton>
        </Stack>
    );
}

export function ChangeRow({ oldValue, newValue }) {
    const oldText = useMemo(() => stringifySafe(oldValue), [oldValue]);
    const newText = useMemo(() => stringifySafe(newValue), [newValue]);

    // Критерии компактного режима: экран >= 900px и оба значения не слишком длинные
    const upMd = useMediaQuery("(min-width:900px)");
    const compact = upMd && oldText.length <= 40 && newText.length <= 40;

    return (
        <Stack
            spacing={compact ? 1 : 0.5}
            sx={{ mt: 0.5, width: "100%" }}
            // В компактном — одна строка, иначе — два блока один под другим
            direction={compact ? "row" : "column"}
            alignItems={compact ? "center" : "flex-start"}
            flexWrap={compact ? "wrap" : "nowrap"}
        >
            {compact ? (
                // Компактный: Было → Стало в одну строку
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Было:</Typography>
                    <ChipValue text={oldText} />
                    <ArrowRightAltIcon fontSize="small" />
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Стало:</Typography>
                    <ChipValue text={newText} />
                </Stack>
            ) : (
                // Расширенный: две строки с разделителем
                <>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                        <Typography variant="body2" sx={{ opacity: 0.8, minWidth: 56 }}>Было:</Typography>
                        <ChipValue text={oldText} />
                    </Stack>

                    <Divider flexItem sx={{ my: 0.5 }} />

                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                        <Typography variant="body2" sx={{ opacity: 0.8, minWidth: 56 }}>Стало:</Typography>
                        <ChipValue text={newText} />
                    </Stack>
                </>
            )}
        </Stack>
    );
}
